import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Plus, Edit3, Trash2, RefreshCw, GraduationCap } from "lucide-react";
import toast from "react-hot-toast";
import {
  card,
  microLabel,
  sectionTitle,
  accentBarSection,
  bodySecondary,
  motionStaggerChild,
  emptyState,
  iconSize,
} from "../../../../../lib/design-system";
import { cn } from "../../../../../lib/utils";
import {
  listPEOs,
  createPEO,
  updatePEO,
  deletePEO,
} from "../../../../../services/program.service";
import OutcomeForm from "../OutcomeForm";

const PEOsTab = ({ programId }) => {
  const [peos, setPeos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const fetchPEOs = async () => {
    try {
      setLoading(true);
      const data = await listPEOs(programId);
      setPeos(data.peos || []);
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to load PEOs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPEOs();
  }, [programId]);

  const handleDelete = async (peo) => {
    if (!window.confirm(`Delete ${peo.code}?`)) return;
    try {
      await deletePEO(programId, peo._id);
      toast.success(`${peo.code} deleted`);
      fetchPEOs();
    } catch (err) {
      toast.error(err.response?.data?.error || "Delete failed");
    }
  };

  const handleFormSubmit = async (payload) => {
    if (editing) {
      await updatePEO(programId, editing._id, payload);
    } else {
      await createPEO(programId, payload);
    }
    fetchPEOs();
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
        <div>
          <h2 className={cn(sectionTitle, "text-xl sm:text-2xl")}>
            Program Educational Objectives
          </h2>
          <div className={cn(accentBarSection, "mt-2")} />
          <p className={cn(bodySecondary, "mt-3 max-w-2xl")}>
            What graduates are expected to attain 3 – 5 years after graduation.
            Typically 3 – 5 PEOs per program. Indirect attainment is measured
            via alumni / employer surveys (deferred to v1.5).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchPEOs}
            className="px-3 py-2 rounded-sm border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            aria-label="Refresh"
          >
            <RefreshCw className={iconSize.md} />
          </button>
          <button
            onClick={() => {
              setEditing(null);
              setShowForm(true);
            }}
            className="inline-flex items-center gap-2 bg-accent1 text-white font-semibold px-4 py-2 rounded-sm hover:opacity-90 transition-opacity"
          >
            <Plus className={iconSize.md} />
            Add PEO
          </button>
        </div>
      </div>

      {loading ? (
        <SkeletonList />
      ) : peos.length === 0 ? (
        <div className={emptyState}>
          <div className="mx-auto h-14 w-14 rounded-sm bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-4">
            <GraduationCap className="h-7 w-7 text-gray-400" />
          </div>
          <div className="font-bold text-gray-700 dark:text-gray-200">
            No PEOs defined yet
          </div>
          <div className={cn(bodySecondary, "mt-1")}>
            Add 3 – 5 educational objectives that align with your institution's
            vision and mission.
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {peos.map((peo, i) => (
            <motion.div
              key={peo._id}
              {...motionStaggerChild(i, 0)}
              className={cn(card, "p-5 group")}
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <div className="h-1 w-8 bg-accent1 rounded-sm mb-2" />
                  <div className={microLabel}>{peo.code}</div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-800 dark:text-gray-200 leading-relaxed">
                    {peo.statement}
                  </p>
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                  <button
                    onClick={() => {
                      setEditing(peo);
                      setShowForm(true);
                    }}
                    className="p-2 rounded-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                    title="Edit"
                    aria-label="Edit"
                  >
                    <Edit3 className={iconSize.sm} />
                  </button>
                  <button
                    onClick={() => handleDelete(peo)}
                    className="p-2 rounded-sm hover:bg-red-50 dark:hover:bg-red-900/20 text-red-600"
                    title="Delete"
                    aria-label="Delete"
                  >
                    <Trash2 className={iconSize.sm} />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {showForm && (
        <OutcomeForm
          type="PEO"
          outcome={editing}
          onSubmit={handleFormSubmit}
          onClose={() => {
            setShowForm(false);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
};

const SkeletonList = () => (
  <div className="space-y-3">
    {[0, 1, 2].map((i) => (
      <div key={i} className={cn(card, "p-5")}>
        <div className="flex gap-4">
          <div className="space-y-2">
            <div className="h-1 w-8 bg-gray-200 dark:bg-gray-700 rounded-sm animate-pulse" />
            <div className="h-3 w-10 bg-gray-200 dark:bg-gray-700 rounded-sm animate-pulse" />
          </div>
          <div className="flex-1 space-y-2">
            <div className="h-3 w-full bg-gray-100 dark:bg-gray-800 rounded-sm animate-pulse" />
            <div className="h-3 w-3/4 bg-gray-100 dark:bg-gray-800 rounded-sm animate-pulse" />
          </div>
        </div>
      </div>
    ))}
  </div>
);

export default PEOsTab;
